(() => {
  'use strict';
  if (window.YoLinkProductController) return;
  const instances = new Map();
  const mount = root => {
    if (instances.has(root)) return;
    let productData, variantPrices;
    try {
      productData = JSON.parse(root.querySelector('[data-yl-product-json]').textContent);
      variantPrices = JSON.parse(root.querySelector('[data-yl-variant-prices]').textContent);
      if (!Array.isArray(productData?.variants) || !variantPrices || typeof variantPrices !== 'object') return;
    } catch { return; }
    const listeners = [];
    const listen = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      listeners.push(() => target.removeEventListener(type, handler, options));
    };
    const reviewRoot = root.querySelector('.yl-product-reviews');
    let disposed = false, observer;
    const clearFilterListeners = new WeakSet();
    // Preserve Judge.me's rating and focusable links in the accessibility tree.
    const normalizeReviewSemantics = () => {
      if (disposed) return;
      reviewRoot?.querySelectorAll('.jdgm-rev-widg__summary-stars[role="img"]').forEach(stars => {
        if (stars.querySelector('a[href],button,[tabindex="0"]')) stars.setAttribute('role', 'group');
      });
      reviewRoot?.querySelectorAll('.jdgm-rev__rating[role="img"][tabindex="0"]').forEach(rating => {
        if (!rating.querySelector('a[href],button,input,select,textarea,[role="button"],[tabindex="0"]')) rating.removeAttribute('tabindex');
      });
      reviewRoot?.querySelectorAll('.jdgm-histogram__clear-filter[tabindex="0"]').forEach(clear => {
        if (!clear.hasAttribute('role')) clear.setAttribute('role', 'button');
        if (clearFilterListeners.has(clear)) return;
        clearFilterListeners.add(clear);
        listen(clear, 'keydown', event => {
          if (event.key !== 'Enter' || event.defaultPrevented) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          clear.click();
        }, true);
      });
    };
    const select = root.querySelector('[data-yl-variant-select]');
    const variantControl = select || root.querySelector('[data-yl-variant-input]');
    const initialId = variantControl?.value;
    const optionFields = root.querySelector('[data-yl-option-fields]');
    const nativeOptionField = root.querySelector('[data-yl-native-option-field]');
    const optionControls = [...root.querySelectorAll('[data-yl-option-index]')];
    const layeredOptions = optionControls.length > 1 &&
      optionControls.every((control, index) => Number(control.dataset.ylOptionIndex) === index) &&
      productData.variants.every(variant => Array.isArray(variant.options) && variant.options.length === optionControls.length);
    if (layeredOptions && optionFields && nativeOptionField) {
      optionFields.hidden = false;
      nativeOptionField.hidden = true; // The native name=id select remains the submitted identity.
    }
    const kitChoices = root.querySelector('[data-yl-kit-choices]');
    const kitButtons = [...root.querySelectorAll('[data-yl-kit-choice]')];
    const nativeKits = !!(kitChoices && select && kitButtons.length === productData.variants.length &&
      kitButtons.every(button => productData.variants.some(variant => String(variant.id) === button.dataset.ylKitChoice)));
    if (nativeKits && nativeOptionField) {
      kitChoices.hidden = false;
      nativeOptionField.hidden = true;
    }
    const price = root.querySelector('[data-yl-price]');
    const compare = root.querySelector('[data-yl-compare]');
    const stock = root.querySelector('[data-yl-stock]');
    const add = root.querySelector('[data-yl-add]');
    const restock = root.querySelector('[data-yl-restock]');
    const accelerated = root.querySelector('[data-yl-accelerated]');
    const mediaFallback = root.querySelector('[data-yl-media-fallback]');
    const nativePrimaryId = root.querySelector('[data-yl-gallery]')?.dataset.ylNativePrimary;
    const variantInformation = [...root.querySelectorAll('[data-yl-variant-information]')];
    const purchaseGuidance = [...root.querySelectorAll('[data-yl-buy-guidance]')];
    const restockStatus = root.querySelector('[data-yl-restock-status]');
    let legacyIds = {};
    try {
      const legacy = root.querySelector('[data-yl-legacy-variant-ids]');
      if (legacy) legacyIds = JSON.parse(legacy.textContent) || {};
    } catch { legacyIds = {}; }

    let activeVariantId = String(initialId || '');
    const mediaAllowed = item => {
      const owners = item.dataset.ylMediaVariants || '';
      return !owners || owners.includes('|' + activeVariantId + '|');
    };
    const orderedGallery = root.querySelector('[data-yl-gallery-order-map]');
    const galleryOrders = new Map((orderedGallery?.dataset.ylGalleryOrderMap || '').trim().split('~').filter(Boolean).map(row => {
      const [id, media] = row.split('^');
      return [id, (media || '').split(',')];
    }));
    const filterVariantMedia = id => {
      activeVariantId = String(id);
      const order = galleryOrders.get(activeVariantId);
      if (order && orderedGallery) {
        const ranks = new Map(order.map((mediaId, index) => [mediaId, index]));
        for (const selector of ['[data-yl-media-panel]', '.yl-product-gallery__thumb']) {
          const items = [...orderedGallery.querySelectorAll(selector)];
          items.sort((a, b) => (ranks.get(a.dataset.ylMediaPanel || a.dataset.mediaId) ?? Infinity) -
            (ranks.get(b.dataset.ylMediaPanel || b.dataset.mediaId) ?? Infinity));
          for (const item of items) item.parentNode.appendChild(item);
        }
      }
      root.querySelectorAll('.yl-product-gallery__thumb').forEach(thumb => {
        thumb.hidden = !mediaAllowed(thumb);
      });
    };
    const activateMedia = id => {
      const panels = [...root.querySelectorAll('[data-yl-media-panel]')];
      const panel = panels.find(item => item.dataset.ylMediaPanel === String(id));
      if (!panel || !mediaAllowed(panel)) return;
      panels.forEach(item => {
        item.hidden = item !== panel;
        if (item.hidden) item.querySelectorAll('video').forEach(video => video.pause());
      });
      root.querySelectorAll('.yl-product-gallery__thumb').forEach(item => {
        const active = item.dataset.mediaId === String(id);
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
    };
    const variants = new Map(productData.variants.map(item => [String(item?.id), item]));
    const resolveId = id => variants.has(String(id)) ? String(id) :
      Object.hasOwn(legacyIds, String(id)) && variants.has(String(legacyIds[id])) ? String(legacyIds[id]) : null;
    const renderVariant = (id, updateUrl = false, preserveNativePrimary = false) => {
      const resolved = resolveId(id);
      if (!resolved) return false;
      const variant = variants.get(resolved);
      const formatted = variantPrices[resolved];
      if (!formatted || typeof formatted.price !== 'string') return false;
      if (variantControl) variantControl.value = resolved; // This is the native product-form id field.
      if (nativeKits) kitButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ylKitChoice === resolved)));
      if (layeredOptions) optionControls.forEach((control, index) => {
        control.value = variant.options[index];
        for (const option of control.options) {
          const matches = productData.variants.filter(candidate => candidate.options[index] === option.value &&
            candidate.options.slice(0, index).every((value, previous) => value === variant.options[previous]));
          option.hidden = matches.length === 0;
          option.disabled = matches.length === 0;
          option.textContent = option.value + (matches.length && matches.every(candidate => !candidate.available) ? ' — Sold out' : '');
        }
      });
      if (price) price.textContent = formatted.price;
      if (compare) {
        compare.hidden = !(variant.compare_at_price && variant.compare_at_price > variant.price);
        if (!compare.hidden) compare.textContent = formatted.compare;
      }
      if (stock) {
        stock.textContent = variant.available ? 'In stock' : 'Currently unavailable';
        stock.classList.toggle('is-unavailable', !variant.available);
      }
      if (add) {
        add.disabled = !variant.available;
        add.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      if (restock) restock.hidden = !!variant.available;
      if (accelerated) accelerated.hidden = !variant.available;
      if (restockStatus) { restockStatus.hidden = true; restockStatus.textContent = ''; }
      variantInformation.forEach(panel => {
        panel.hidden = panel.dataset.ylVariantInformation !== resolved;
      });
      purchaseGuidance.forEach(panel => {
        panel.hidden = panel.dataset.ylBuyGuidance !== resolved;
      });
      filterVariantMedia(resolved);
      const eligiblePanels = [...root.querySelectorAll('[data-yl-media-panel]')].filter(mediaAllowed);
      const scopedGallery = [...root.querySelectorAll('[data-yl-media-panel]')]
        .some(panel => Object.hasOwn(panel.dataset, 'ylMediaVariants'));
      const mediaId = variant.featured_media?.id;
      const mediaExists = mediaId && eligiblePanels
        .some(panel => panel.dataset.ylMediaPanel === String(mediaId));
      const nativePrimaryExists = preserveNativePrimary && nativePrimaryId && eligiblePanels
        .some(panel => panel.dataset.ylMediaPanel === nativePrimaryId);
      if (nativePrimaryExists) activateMedia(nativePrimaryId);
      else if (mediaExists) activateMedia(mediaId);
      else if (scopedGallery && eligiblePanels.length) activateMedia(eligiblePanels[0].dataset.ylMediaPanel);
      else if (variantInformation.length || scopedGallery) {
        root.querySelectorAll('[data-yl-media-panel]').forEach(panel => {
          panel.hidden = true;
          panel.querySelectorAll('video').forEach(video => video.pause());
        });
        root.querySelectorAll('.yl-product-gallery__thumb').forEach(thumb => {
          thumb.classList.toggle('is-active', false);
          thumb.setAttribute('aria-pressed', 'false');
        });
      }
      if (mediaFallback) mediaFallback.hidden = !!(nativePrimaryExists || mediaExists || scopedGallery && eligiblePanels.length);
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', resolved);
        history.replaceState(history.state, '', url);
      }
      root.dispatchEvent?.(new Event('yl:variant-change', { bubbles: true })); // Page add-ons (e.g. the Trapeezee sticky bar) re-read the form.
      return true;
    };
    listen(select, 'change', () => renderVariant(select.value, true));
    if (nativeKits) kitButtons.forEach(button => listen(button, 'click', () => {
      select.value = button.dataset.ylKitChoice;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    if (layeredOptions) optionControls.forEach((control, index) => listen(control, 'change', () => {
      const chosen = optionControls.map(item => item.value);
      const matches = productData.variants.filter(variant =>
        variant.options.slice(0, index + 1).every((value, position) => value === chosen[position]));
      const selected = matches.find(variant => variant.options.every((value, position) => value === chosen[position])) ||
        matches.find(variant => variant.available) || matches[0];
      if (selected && select) {
        select.value = String(selected.id);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (selected) renderVariant(selected.id, true);
      else renderVariant(variantControl?.value); // Restore valid controls if an invalid combination was requested.
    }));
    listen(window, 'popstate', () => {
      const requested = new URL(window.location.href).searchParams.get('variant');
      if (requested) renderVariant(requested, Object.hasOwn(legacyIds, requested));
      else if (initialId) renderVariant(initialId, false, true);
    });
    listen(restock, 'click', () => {
      const variant = variants.get(String(variantControl?.value));
      if (!variant || variant.available) return;
      if (window.BIS?.popup?.show) window.BIS.popup.show({ variantId: variant.id });
      else if (restockStatus) {
        restockStatus.textContent = 'Restock sign-up is temporarily unavailable.';
        restockStatus.hidden = false;
      }
    });
    if (initialId) filterVariantMedia(initialId);
    const requested = new URL(window.location.href).searchParams.get('variant');
    if (requested && resolveId(requested)) renderVariant(requested, Object.hasOwn(legacyIds, requested));
    else if ((variantInformation.length || purchaseGuidance.length) && initialId) renderVariant(initialId, false, !requested);
    root.querySelectorAll('.yl-product-gallery__thumb').forEach(button =>
      listen(button, 'click', () => activateMedia(button.dataset.mediaId)));
    normalizeReviewSemantics();
    if (reviewRoot) {
      observer = new MutationObserver(normalizeReviewSemantics);
      observer.observe(reviewRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['role', 'tabindex'] });
    }
    instances.set(root, () => {
      disposed = true;
      listeners.forEach(remove => remove());
      observer?.disconnect();
      root.querySelectorAll('video').forEach(video => video.pause());
      instances.delete(root);
    });
  };
  const scan = () => document.querySelectorAll('[data-yl-product-root]').forEach(mount);
  window.YoLinkProductController = { scan };
  scan();
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of instances) if (event.target === root || event.target.contains(root)) dispose();
  });
})();
